/**
 * The CI/CD standard per repository as the github-insights backend answers it
 * (DX-225, specs/repository-standard.md; how each rule is decided is
 * docs/repository-standard.md).
 *
 * The Repositories tab and a component's CI/CD tab both read it from there
 * rather than measuring anything themselves, so a repository's card says what
 * its row says.
 */

export type RuleId =
  | 'gate'
  | 'deployGated'
  | 'deployCanFail'
  | 'nodePinned'
  | 'lockfile'
  | 'owner'
  | 'readme'
  | 'catalogInfo'
  | 'mkdocs'
  | 'dependabot'
  | 'rulesetFile'
  | 'permissions'
  | 'actionsPinned'
  | 'mainProtected'
  | 'tokenReadOnly';

export type MarkState = 'pass' | 'fail' | 'na' | 'exempt' | 'unknown';

export type RepoMark = {
  state: MarkState;
  /** What was found, in a sentence. */
  detail: string;
  /** Where it can be seen, when there is such a place. */
  link?: string;
};

export type Rule = {
  id: RuleId;
  title: string;
  /** The section of the standard, `§4.1`. */
  section: string;
  /** Who can change it: the repository's team, or the organisation's admin. */
  fixedBy: 'team' | 'admin';
};

export type Exemption = {
  text: string;
  sections: string[];
  rules: RuleId[];
};

export type RepoRow = {
  slug: string;
  org: string;
  name: string;
  url: string;
  /** The team its catalog-info.yaml names, `group:<namespace>/<name>`. */
  owner?: string;
  empty: boolean;
  marks: Record<RuleId, RepoMark>;
  teamRulesMet: boolean | null;
  exemptions: Exemption[];
  usesSharedGate: boolean;
  unreadable?: string;
};

export type Tally = Record<MarkState, number>;

export type OrgState = {
  org: string;
  state: 'read' | 'unread';
  repos: number;
};

/** One day's figures, as the history keeps them. */
export type HistoryDay = {
  day: string;
  teamRules: { met: number; notMet: number; unknown: number };
  rules: Record<RuleId, { pass: number; fail: number }>;
};

export type RepoStandardSnapshot = {
  /** When the repositories were last measured; null before the first time. */
  generatedAt: string | null;
  rules: Rule[];
  orgs: OrgState[];
  repos: RepoRow[];
  summary: {
    repos: number;
    rules: Record<RuleId, Tally>;
    teamRules: { met: number; notMet: number; unknown: number };
    progress: {
      mainProtected: { protected: number; of: number };
      sharedGate: number;
    };
  };
};

/** One repository's row, for a component's CI/CD tab. */
export type RepoStandard = {
  generatedAt: string | null;
  rules: Rule[];
  orgs: OrgState[];
  /** Null for a repository that is not measured. */
  repo: RepoRow | null;
};

/**
 * Each state in a shape of its own, so colour is never the only difference —
 * the scorecards' three, and two more: a rule that does not apply, and one the
 * README gives a reason for departing from.
 */
export const MARK_LOOK: Record<
  MarkState,
  { glyph: string; word: string; colour: string }
> = {
  pass: { glyph: '✓', word: 'met', colour: '#2EA043' },
  fail: { glyph: '✕', word: 'not met', colour: '#F85149' },
  na: { glyph: '–', word: 'does not apply', colour: 'var(--ui-text-muted)' },
  exempt: { glyph: '§', word: 'exempt', colour: '#D29922' },
  unknown: {
    glyph: '?',
    word: 'not measurable',
    colour: 'var(--ui-text-muted)',
  },
};

/** The row's anchor on the Repositories tab, which a CI/CD card links to. */
export const repoRowIdOf = (slug: string) =>
  `repo-${slug.toLowerCase().replace(/[^a-z0-9-]+/g, '-')}`;

/** The Repositories tab. */
export const REPOSITORIES_VIEW = '/scorecards?view=repositories';

/** A repository's row on the Repositories tab. */
export const repoRowLinkOf = (slug: string) =>
  `${REPOSITORIES_VIEW}#${repoRowIdOf(slug)}`;

/** How many of a rule's marks were met, out of those met or not. */
export const metOf = (tally: Tally) => ({
  met: tally.pass,
  of: tally.pass + tally.fail,
});

/** The team rules a repository does not meet. */
export const missedTeamRules = (row: RepoRow, rules: Rule[]): Rule[] =>
  rules.filter(r => r.fixedBy === 'team' && row.marks[r.id].state === 'fail');
